import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, map, Observable, tap} from 'rxjs';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';

type PublicationRequestSummaryDto = {
  itemId: string;
  itemName: string;
  thumbnailUrl: string | null;
  locationId: string;
  locationName: string;
  ownerId: string;
  ownerEmail: string;
  ownerUsername: string;
  requestedAt: string | null;
};

type PublicationImageDto = {
  id: string;
  originalUrl: string;
  thumbnailUrl: string;
  url: string;
  isMain: boolean;
  useInAi: boolean;
};

type SeoTranslationDto = {
  languageCode: string;
  seoTitle: string | null;
  seoDescription: string | null;
  seoKeywords: string | null;
};

type SeoTranslateRequestDto = {
  SourceLanguageCode: string;
  Translations: Array<{
    LanguageCode: string;
    SeoTitle: string | null;
    SeoDescription: string | null;
    SeoKeywords: string | null;
  }>;
};

type PublicationRequestReviewDto = {
  itemId: string;
  itemName: string;
  itemDescription: string | null;
  expirationDate: string | null;
  images: PublicationImageDto[];
  locationId: string;
  locationName: string;
  ownerId: string;
  ownerEmail: string;
  ownerUsername: string;
  quantity: number;
  unitLabel: string | null;
  requestedAt: string | null;
  status: 'Pending';
  seoTranslations: SeoTranslationDto[];
};

export type PublicationRequestSummary = PublicationRequestSummaryDto;
export type SeoTranslation = {
  languageCode: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
};
export type PublicationRequestReview = Omit<PublicationRequestReviewDto, 'seoTranslations'> & {
  seoTranslations: SeoTranslation[];
};

function requireData<T>(payload: ApiResponse<T>, message: string): T {
  if (!payload.success || payload.data === undefined) {
    throw new Error(message);
  }

  return payload.data;
}

function mapTranslation(dto: SeoTranslationDto): SeoTranslation {
  return {
    languageCode: dto.languageCode,
    seoTitle: dto.seoTitle ?? '',
    seoDescription: dto.seoDescription ?? '',
    seoKeywords: dto.seoKeywords ?? '',
  };
}

function hasValue(value: string): boolean {
  return value.trim().length > 0;
}

function hasMissingMetadata(translation: SeoTranslation): boolean {
  return !hasValue(translation.seoTitle)
    || !hasValue(translation.seoDescription)
    || !hasValue(translation.seoKeywords);
}

function mergeMissingMetadata(original: SeoTranslation, generated?: SeoTranslation): SeoTranslation {
  return {
    ...original,
    seoTitle: hasValue(original.seoTitle) ? original.seoTitle : generated?.seoTitle ?? '',
    seoDescription: hasValue(original.seoDescription) ? original.seoDescription : generated?.seoDescription ?? '',
    seoKeywords: hasValue(original.seoKeywords) ? original.seoKeywords : generated?.seoKeywords ?? '',
  };
}

@Injectable({providedIn: 'root'})
export class PublicationModerationService {
  private readonly pendingCountSubject = new BehaviorSubject(0);
  readonly pendingCount$ = this.pendingCountSubject.asObservable();

  constructor(private readonly httpClient: HttpClient) {}

  getPendingRequests(): Observable<PublicationRequestSummary[]> {
    return this.httpClient.get<ApiResponse<PublicationRequestSummaryDto[]>>(
      buildApiUrl('/v1/moderation/publication-requests'),
    ).pipe(
      map((payload) => requireData(payload, 'Publication requests response is missing.').map((item) => ({...item}))),
      tap((requests) => this.pendingCountSubject.next(requests.length)),
    );
  }

  refreshPendingCount(): Observable<number> {
    return this.getPendingRequests().pipe(map((requests) => requests.length));
  }

  resetPendingCount(): void {
    this.pendingCountSubject.next(0);
  }

  incrementPendingCount(): void {
    this.pendingCountSubject.next(this.pendingCountSubject.value + 1);
  }

  getReview(itemId: string): Observable<PublicationRequestReview> {
    return this.httpClient.get<ApiResponse<PublicationRequestReviewDto>>(
      buildApiUrl(`/v1/moderation/publication-requests/${itemId}`),
    ).pipe(map((payload) => {
      const dto = requireData(payload, 'Publication request review response is missing.');
      return {...dto, seoTranslations: dto.seoTranslations.map(mapTranslation)};
    }));
  }

  approve(itemId: string, translations: SeoTranslation[]): Observable<void> {
    return this.review(itemId, 'Approve', null, translations);
  }

  reject(itemId: string, reason: string): Observable<void> {
    return this.review(itemId, 'Reject', reason.trim(), []);
  }

  translateSeo(itemId: string, translations: SeoTranslation[]): Observable<SeoTranslation[]> {
    const request: SeoTranslateRequestDto = {
      SourceLanguageCode: 'en',
      Translations: translations.map((translation) => {
        const shouldTranslate = translation.languageCode !== 'en' && hasMissingMetadata(translation);
        return {
          LanguageCode: translation.languageCode,
          SeoTitle: shouldTranslate ? null : translation.seoTitle || null,
          SeoDescription: shouldTranslate ? null : translation.seoDescription || null,
          SeoKeywords: shouldTranslate ? null : translation.seoKeywords || null,
        };
      }),
    };

    return this.httpClient.post<ApiResponse<SeoTranslationDto[]>>(
      buildApiUrl(`/v1/moderation/publication-requests/${itemId}/seo/translate`),
      request,
    ).pipe(map((payload) => {
      const generated = requireData(payload, 'SEO translation response is missing.')
        .map(mapTranslation);
      const generatedByLanguage = new Map(generated.map((translation) => [translation.languageCode, translation]));
      return translations.map((translation) => mergeMissingMetadata(
        translation,
        generatedByLanguage.get(translation.languageCode),
      ));
    }));
  }

  private review(itemId: string, action: 'Approve' | 'Reject', rejectionReason: string | null, translations: SeoTranslation[]): Observable<void> {
    return this.httpClient.put<ApiResponse<unknown>>(
      buildApiUrl(`/v1/moderation/publication-requests/${itemId}`),
      {
        Action: action,
        RejectionReason: rejectionReason,
        SeoTranslations: translations.map((translation) => ({
          LanguageCode: translation.languageCode,
          SeoTitle: translation.seoTitle || null,
          SeoDescription: translation.seoDescription || null,
          SeoKeywords: translation.seoKeywords || null,
        })),
      },
    ).pipe(
      map(() => void 0),
      tap(() => this.pendingCountSubject.next(Math.max(0, this.pendingCountSubject.value - 1))),
    );
  }
}
